import { StyleProp, ViewStyle } from "react-native";
import { Contact, ContactModel, GroupModel, MediaModel, MemoryModel } from "common247";

/**
 * Entity types supported for in-text mention references.
 */
export type MentionEntityType = "contact" | "group" | "media" | "memory";

/**
 * Supported in-place sizing options for embedded media (images and videos).
 * Supports standard presets ("25%", "50%", "75%", "100%") and free drag percentage sizing.
 */
export type MediaSize = "25%" | "50%" | "75%" | "100%" | `${number}%` | string;

/**
 * Represents a selectable mention item in the autocomplete popup.
 */
export interface IMentionItem {
  id: string;
  type: MentionEntityType;
  label: string;
  secondaryText?: string;
  icon?: string;
}

/**
 * Parsed token node representing either plain formatted text, a container tag, or an entity chip.
 */
export interface IParsedHtmlNode {
  type: "text" | "chip" | "tag-open" | "tag-close" | "br" | "table" | "media-embed";
  tagName?: string;
  entityType?: MentionEntityType;
  id?: string;
  label?: string;
  rawText?: string;
  formats?: IEditorFormatState;
  size?: MediaSize;
  width?: number | string;
  height?: number | string;
  align?: "left" | "right" | "center";
  tableData?: {
    headers: string[];
    rows: string[][];
  };
}

/**
 * Inline entity reference token embedded within a block.
 */
export interface IEditorChipToken {
  id: string;
  entityType: MentionEntityType;
  label: string;
  offset: number;
}

/**
 * Inline text formatting flags applied to a block or selection.
 */
export interface IEditorFormatState {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  fontFamily?: string; // e.g. "sans", "serif", "monospace"
  fontSize?: string; // e.g. "small", "normal", "large", "huge"
}

/**
 * Types of structured blocks supported by ThemedEditor.
 */
export type EditorBlockType =
  | "paragraph"
  | "h1"
  | "h2"
  | "h3"
  | "bullet-list"
  | "ordered-list"
  | "quote"
  | "table"
  | "media";

/**
 * Represents a single structured block in ThemedEditor's normal-text document model.
 */
export interface IEditorBlock {
  id: string;
  type: EditorBlockType;
  /**
   * Plain, human-readable text content without raw HTML tags.
   */
  text: string;
  /**
   * Formats applied to this block.
   */
  formats?: IEditorFormatState;
  /**
   * Inline mention chips embedded within this text block.
   */
  chips?: IEditorChipToken[];
  /**
   * For list blocks: individual list items in plain text.
   */
  listItems?: string[];
  /**
   * For table blocks: 2D matrix of cell contents.
   */
  table?: {
    headers: string[];
    rows: string[][];
  };
  /**
   * For media blocks: reference ID, display name, and in-place size.
   */
  media?: {
    mediaId: string;
    title: string;
    mediaType?: "image" | "video";
    size: MediaSize;
    width?: number | string;
    height?: number | string;
    align?: "left" | "right" | "center";
    downloadUrl?: string;
  };
}

/**
 * @deprecated Use IThemedChipProps directly.
 * Props for the legacy ThemedMentionChip component, consolidated into ThemedChip.
 */
export interface IThemedMentionChipProps {
  type?: MentionEntityType | string;
  id?: string;
  label?: string;
  onPress?: ((type: MentionEntityType, id: string, label: string) => void) | (() => void);
  /**
   * Optional one-tap removal callback in edit mode.
   */
  onRemove?: () => void;
  style?: StyleProp<ViewStyle>;
  compact?: boolean;
  /**
   * If true, the referenced item could not be found and will be marked with a red underline.
   */
  notFound?: boolean;
  /**
   * Whether the chip is draggable.
   */
  draggable?: boolean;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
  [key: string]: any;
}

/**
 * Props for the ThemedEditor component.
 */
export interface IThemedEditorProps {
  /**
   * The underlying HTML string content with embedded entity tags.
   */
  value?: string;

  /**
   * Alias for value, used in collaborative editor workflows.
   */
  initialContent?: string;

  /**
   * Canonical identifier of the entity being edited (e.g. memoryId, mediaId, groupId) for collaborative syncing.
   */
  entityId?: string;

  /**
   * Domain entity type for room and collection routing (e.g. "memory", "media", "group", "contact").
   */
  entityType?: "memory" | "media" | "group" | "contact" | string;

  /**
   * Whether collaborative multi-user editing with real-time presence is enabled.
   * Defaults to true if entityId is present.
   */
  collaborative?: boolean;

  /**
   * Optional focus handler to track active editing state.
   */
  onFocus?: () => void;

  /**
   * Optional blur handler to clear active editing state.
   */
  onBlur?: () => void;

  /**
   * Optional testID.
   */
  testID?: string;

  /**
   * Callback fired when the sanitized HTML content changes.
   */
  onChange?: (html: string) => void;

  /**
   * Optional callback fired when text selection or cursor position changes.
   */
  onSelectionChange?: (selection: { start: number; end: number }) => void;

  /**
   * Operational display mode:
   * - "edit": Interactive rich text editor with mention triggers.
   * - "view": Read-only presentation rendering formatted text and interactive chips.
   * - "preview": Split / preview view displaying both editor and live rendered output.
   */
  mode?: "edit" | "view" | "preview";

  /**
   * Whether the editor is editable. Defaults to true when mode is 'edit'.
   */
  editable?: boolean;

  /**
   * Placeholder text for empty editor state.
   */
  placeholder?: string;

  /**
   * List of available contacts for '@' mention resolution.
   */
  contacts?: (Contact | ContactModel | { id?: string; displayName?: string })[];

  /**
   * List of available groups for '@' mention resolution.
   */
  groups?: (GroupModel | { id?: string; name?: string })[];

  /**
   * List of available media items for '+' mention resolution.
   */
  media?: (MediaModel | { id?: string; title?: string; contentType?: string; downloadUrl?: string })[];

  /**
   * List of available memories for '#' mention resolution.
   */
  memories?: (MemoryModel | { id?: string; title?: string })[];

  /**
   * Optional ID of the memory currently being edited, used to associate newly pasted media.
   */
  activeMemoryId?: string;

  /**
   * Handler invoked when new media is pasted or dropped into the editor.
   * Must upload/register the media and return its created ID and title.
   */
  onMediaUpload?: (file: any, memoryId?: string) => Promise<{ id: string; title: string; url?: string }>;

  /**
   * Custom navigation callback when a user taps an entity chip in view mode.
   * Defaults to Expo Router deep linking.
   */
  onChipPress?: (type: MentionEntityType, id: string, label: string) => void;

  /**
   * Optional custom validator to check if a referenced entity ID exists / is accessible.
   * If omitted, checks against the provided contacts, groups, media, and memories arrays.
   */
  isItemFound?: (type: MentionEntityType, id: string) => boolean;

  /**
   * Custom style container override.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Whether to display the live raw HTML markup view below the editor.
   */
  showRawHtml?: boolean;

  /**
   * Accessibility label for the control.
   */
  accessibilityLabel?: string;

  /**
   * If true, editor height dynamically fits content between minLines and maxScreenRatio.
   * Standard for all description fields.
   */
  dynamicHeight?: boolean;

  /**
   * Minimum height in lines of normal text. Defaults to 4.
   */
  minLines?: number;

  /**
   * Maximum height as a ratio of screen/window height. Defaults to 0.75 (3/4 of screen height).
   */
  maxScreenRatio?: number;
}
